import React, { useState } from 'react';
import { runSeeder } from '../../../scripts/seedTestCompanies';
import { SocialSeederService } from '../../../core/services/firebase/SocialSeederService';
import { MagicLinkService } from '../../../core/services/auth/MagicLinkService';
import { Terminal, Play, Users, MessageSquare, Zap, BarChart3, Database, Key } from 'lucide-react';

export const DeveloperToolsPage: React.FC = () => {
    const [logs, setLogs] = useState<{msg: string, type?: 'info' | 'success' | 'error'}[]>([]);
    const [isRunning, setIsRunning] = useState(false);
    const [progress, setProgress] = useState(0);
    const [activeTab, setActiveTab] = useState<'tenants' | 'social' | 'leads'>('social');
    const [targetHandle, setTargetHandle] = useState('');
    const [generatedUrl, setGeneratedUrl] = useState('');
    
    // Social Seeding Params
    const [postCount, setPostCount] = useState(10);
    const [profileCount, setProfileCount] = useState(5);
    const [daysBack, setDaysBack] = useState(30);

    const log = (msg: string, type: 'info' | 'success' | 'error' = 'info', prog?: number) => {
        setLogs(prev => [...prev, { msg, type }]);
        if (prog !== undefined) setProgress(prog);
    };

    const handleRunTenantSeeder = async () => {
        if (isRunning) return;
        setIsRunning(true);
        setLogs([{ msg: '🚀 Starting Tenant Seeder...', type: 'info' }]);
        setProgress(0);
        
        try {
            await runSeeder((msg) => log(msg));
            log('✅ Tenant seeding complete!', 'success', 100);
        } catch (error: any) {
            log(`❌ Error: ${error.message}`, 'error');
        } finally {
            setIsRunning(false);
        }
    };

    const handleSeedSocial = async (type: 'profiles' | 'posts' | 'full') => {
        if (isRunning) return;
        setIsRunning(true);
        setLogs([{ msg: `🚀 Starting Social Seeding: ${type}...`, type: 'info' }]);
        setProgress(0);

        try {
            if (type === 'profiles' || type === 'full') {
                await SocialSeederService.seedProfiles(profileCount, (msg, p) => log(msg, 'info', p));
            }
            if (type === 'posts' || type === 'full') {
                await SocialSeederService.seedPosts(postCount, daysBack, (msg, p) => log(msg, 'info', p));
            }
            log('✅ Social seeding complete!', 'success', 100);
        } catch (error: any) {
            log(`❌ Error: ${error.message}`, 'error');
        } finally {
            setIsRunning(false);
        }
    };

    return (
        <>
            <div className="max-w-5xl mx-auto space-y-8 pb-20">
                {/* Cabeçalho */}
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                             <div className="bg-blue-600 p-2 rounded-lg">
                                <Database className="size-5 text-white" />
                             </div>
                             <span className="text-sm font-bold text-blue-600 uppercase tracking-wider">Ferramentas Dev</span>
                        </div>
                        <h1 className="text-4xl font-black text-slate-900 dark:text-white leading-none">
                            Painel do Desenvolvedor
                        </h1>
                        <p className="text-slate-500 dark:text-slate-400 mt-3 text-lg">
                            Gerencie dados de teste e simule a evolução da plataforma.
                        </p>
                    </div>
                    
                    <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-xl">
                        <button 
                            onClick={() => setActiveTab('social')}
                            className={`px-6 py-2.5 rounded-lg font-bold transition-all ${activeTab === 'social' ? 'bg-white dark:bg-slate-700 shadow-sm text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            Rede Social
                        </button>
                        <button 
                            onClick={() => setActiveTab('leads')}
                            className={`px-6 py-2.5 rounded-lg font-bold transition-all ${activeTab === 'leads' ? 'bg-white dark:bg-slate-700 shadow-sm text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            Convites
                        </button>
                    </div>
                </div>

                <div className="grid lg:grid-cols-12 gap-8">
                    {/* Controls Column */}
                    <div className="lg:col-span-7 space-y-6">
                        {activeTab === 'social' ? (
                            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-8 shadow-sm">
                                <div className="flex items-center gap-3 mb-8">
                                    <div className="p-3 bg-indigo-50 dark:bg-indigo-900/30 rounded-xl">
                                        <Zap className="size-6 text-indigo-600" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-slate-900 dark:text-white">Injeção de Dados</h3>
                                        <p className="text-sm text-slate-500 dark:text-slate-400">Popule a rede social com perfis e posts de teste</p>
                                    </div>
                                </div>

                                <div className="space-y-6">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Perfis a Criar</label>
                                            <input 
                                                type="number" 
                                                value={profileCount}
                                                id="profileCount"
                                                title="Quantidade de perfis"
                                                aria-label="Quantidade de perfis a criar"
                                                onChange={(e) => setProfileCount(parseInt(e.target.value))}
                                                className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none transition-all text-slate-900 dark:text-white"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Posts a Criar</label>
                                            <input 
                                                type="number" 
                                                value={postCount}
                                                id="postCount"
                                                title="Quantidade de posts"
                                                aria-label="Quantidade de posts a criar"
                                                onChange={(e) => setPostCount(parseInt(e.target.value))}
                                                className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none transition-all text-slate-900 dark:text-white"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">
                                            Período de Distribuição (dias)
                                        </label>
                                        <div className="flex items-center gap-4">
                                            <input 
                                                type="range" 
                                                min="1" 
                                                max="365" 
                                                value={daysBack}
                                                id="daysBack"
                                                title="Período de crescimento"
                                                aria-label="Período de evolução em dias"
                                                onChange={(e) => setDaysBack(parseInt(e.target.value))}
                                                className="flex-1 accent-blue-600"
                                            />
                                            <span className="w-16 text-center font-bold text-blue-600 bg-blue-50 dark:bg-blue-900/30 py-1 rounded-lg border border-blue-100 dark:border-blue-800 italic">
                                                {daysBack}d
                                            </span>
                                        </div>
                                        <p className="text-xs text-slate-400 mt-2">Os posts serão distribuídos aleatoriamente nos últimos {daysBack} dias.</p>
                                    </div>

                                    <div className="grid grid-cols-2 gap-3 pt-4">
                                        <button
                                            onClick={() => handleSeedSocial('profiles')}
                                            disabled={isRunning}
                                            className="flex flex-col items-center gap-2 p-6 rounded-2xl border-2 border-slate-100 hover:border-blue-200 hover:bg-blue-50 transition-all group disabled:opacity-50"
                                        >
                                            <Users className="size-8 text-slate-400 group-hover:text-blue-600" />
                                            <span className="font-bold text-slate-700 group-hover:text-blue-900">Criar Perfis</span>
                                        </button>
                                        <button
                                            onClick={() => handleSeedSocial('posts')}
                                            disabled={isRunning}
                                            className="flex flex-col items-center gap-2 p-6 rounded-2xl border-2 border-slate-100 hover:border-blue-200 hover:bg-blue-50 transition-all group disabled:opacity-50"
                                        >
                                            <MessageSquare className="size-8 text-slate-400 group-hover:text-blue-600" />
                                            <span className="font-bold text-slate-700 group-hover:text-blue-900">Criar Posts</span>
                                        </button>
                                    </div>
                                    
                                    <button
                                        onClick={() => handleSeedSocial('full')}
                                        disabled={isRunning}
                                        className="w-full py-4 bg-blue-600 text-white font-black rounded-2xl shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all flex items-center justify-center gap-3 disabled:bg-slate-300"
                                    >
                                        <Play className="size-5 fill-current" />
                                        POPULAR TODO O ECOSSISTEMA
                                    </button>
                                </div>
                            </div>
                        ) : activeTab === 'leads' ? (
                            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-8 shadow-sm">
                                <div className="flex items-center gap-3 mb-8">
                                    <div className="p-3 bg-amber-50 dark:bg-amber-900/30 rounded-xl">
                                        <Key className="size-6 text-amber-600" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-slate-900 dark:text-white">Gerador de Convites</h3>
                                        <p className="text-sm text-slate-500 dark:text-slate-400">Crie links mágicos para onboarding de empresas</p>
                                    </div>
                                </div>
                                
                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Handle do Perfil Alvo</label>
                                        <div className="flex gap-2">
                                            <div className="flex-1 relative">
                                                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-bold">@</span>
                                                <input 
                                                    type="text" 
                                                    value={targetHandle}
                                                    placeholder="pousada_do_rio"
                                                    onChange={(e) => setTargetHandle(e.target.value.replace('@', ''))}
                                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-8 pr-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none transition-all font-bold"
                                                />
                                            </div>
                                            <button
                                                onClick={async () => {
                                                    if (!targetHandle) return;
                                                    setIsRunning(true);
                                                    try {
                                                        const token = await MagicLinkService.generateToken(targetHandle, 'business');
                                                        const url = `${window.location.origin}/invite/${token}`;
                                                        setGeneratedUrl(url);
                                                        log(`✅ Token gerado para @${targetHandle}`, 'success');
                                                    } catch (e: any) {
                                                        log(`❌ Erro ao gerar token: ${e.message}`, 'error');
                                                    } finally {
                                                        setIsRunning(false);
                                                    }
                                                }}
                                                disabled={isRunning || !targetHandle}
                                                className="bg-blue-600 text-white px-6 rounded-xl font-bold hover:bg-blue-700 disabled:opacity-50 transition-all"
                                            >
                                                Gerar
                                            </button>
                                        </div>
                                    </div>

                                    {generatedUrl && (
                                        <div className="bg-slate-900 p-6 rounded-2xl border border-blue-500/30">
                                            <label className="block text-[10px] font-black text-blue-400 uppercase tracking-widest mb-2">Link Mágico (Apenas Convites)</label>
                                            <div className="flex flex-col gap-3">
                                                <code className="text-xs text-blue-100 break-all bg-white/5 p-3 rounded-lg border border-white/10">
                                                    {generatedUrl}
                                                </code>
                                                <div className="flex gap-2">
                                                    <button 
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(generatedUrl);
                                                            log('📋 Link copiado!', 'success');
                                                        }}
                                                        className="flex-1 py-2 bg-blue-500 text-white rounded-lg text-xs font-bold hover:bg-blue-400 transition-all"
                                                    >
                                                        Copiar Link
                                                    </button>
                                                    <button 
                                                        onClick={() => window.open(generatedUrl, '_blank')}
                                                        className="flex-1 py-2 bg-white/10 text-white rounded-lg text-xs font-bold hover:bg-white/20 transition-all border border-white/10"
                                                    >
                                                        Abrir Convite
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-8 shadow-sm">
                                <div className="flex items-center gap-3 mb-8">
                                    <div className="p-3 bg-amber-50 dark:bg-amber-900/30 rounded-xl">
                                        <Database className="size-6 text-amber-600" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-slate-900 dark:text-white">Popular Empresas</h3>
                                        <p className="text-sm text-slate-500 dark:text-slate-400">Adiciona empresas fictícias ao diretório</p>
                                    </div>
                                </div>
                                <p className="text-slate-600 dark:text-slate-400 mb-8 leading-relaxed">
                                    Popula a coleção 'tenant_requests' com empresas fictícias (Pousadas, Barco-Hotéis, etc) para testar o fluxo de aprovação e a visibilidade no portal.
                                </p>
                                <button
                                    onClick={handleRunTenantSeeder}
                                    disabled={isRunning}
                                    className="w-full py-4 bg-slate-900 text-white font-black rounded-2xl hover:bg-slate-800 transition-all flex items-center justify-center gap-3 disabled:bg-slate-300"
                                >
                                    <Play className="size-5 fill-current" />
                                    {isRunning ? 'Populando...' : 'Executar Seeding'}
                                </button>
                            </div>
                        )}

                        <div className="bg-blue-900 rounded-2xl p-8 text-white relative overflow-hidden">
                            <BarChart3 className="absolute -bottom-4 -right-4 size-32 opacity-10" />
                            <h4 className="text-xl font-bold mb-2">Simular Campanha</h4>
                            <p className="text-blue-100 mb-6 opacity-80">Gera uma explosão de atividade simulando uma campanha de marketing de influenciador bem-sucedida.</p>
                            <button className="px-6 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-bold text-sm transition-all backdrop-blur-sm border border-white/20">
                                Em Breve
                            </button>
                        </div>
                    </div>

                    {/* Coluna de Console */}
                    <div className="lg:col-span-5 flex flex-col h-[600px] lg:h-auto">
                        <div className="bg-slate-900 rounded-t-2xl p-4 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className="flex gap-1.5">
                                    <div className="size-3 rounded-full bg-red-500" />
                                    <div className="size-3 rounded-full bg-amber-500" />
                                    <div className="size-3 rounded-full bg-emerald-500" />
                                </div>
                                <span className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-4">Saída do Terminal</span>
                            </div>
                            <button onClick={() => setLogs([])} className="text-xs text-slate-500 hover:text-slate-300 font-bold uppercase">Limpar</button>
                        </div>
                        
                        <div className="flex-1 bg-slate-950 font-mono text-xs p-6 overflow-y-auto section-scroll scroll-smooth border-x border-slate-800">
                             {logs.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full text-slate-700 animate-pulse">
                                    <Terminal className="size-12 mb-4 opacity-20" />
                                    <p className="italic">Aguardando execução...</p>
                                </div>
                            ) : (
                                <div className="space-y-1.5">
                                    {logs.map((log, i) => (
                                        <div 
                                            key={i} 
                                            className={`leading-relaxed border-l-2 pl-3 py-1 ${
                                                log.type === 'success' ? 'text-emerald-400 border-emerald-500/50 bg-emerald-500/5' : 
                                                log.type === 'error' ? 'text-red-400 border-red-500/50 bg-red-500/5' : 
                                                'text-slate-300 border-slate-700/50'
                                            }`}
                                        >
                                            <span className="opacity-40 mr-2">[{new Date().toLocaleTimeString([], { hour12: false })}]</span>
                                            {log.msg}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="bg-slate-900 rounded-b-2xl p-6 border-t border-white/5">
                            <div className="flex items-center justify-between mb-3">
                                <span className="text-xs font-black text-slate-400 uppercase">Status do Motor</span>
                                <span className="text-xs font-black text-blue-400">{Math.round(progress)}%</span>
                            </div>
                            <div className="h-4 bg-slate-800 rounded-full overflow-hidden p-1 shadow-inner">
                                <div 
                                    className={`h-full rounded-full transition-all duration-500 ${isRunning ? 'bg-gradient-to-r from-blue-600 to-indigo-600 animate-pulse' : 'bg-slate-700'}`}
                                    style={{ width: `${progress}%` }}
                                />
                            </div>
                            <div className="mt-4 flex items-center gap-2">
                                <div className={`size-2 rounded-full ${isRunning ? 'bg-emerald-500 animate-ping' : 'bg-slate-600'}`} />
                                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest leading-none">
                                    {isRunning ? 'Processando...' : 'Sistema Ocioso'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
